import { HelpBulb } from '@/features/help';
import { fmt, monthIndex, monthShort } from '@/shared/lib';
import type { SubscriptionDetail } from '../api/types';

/**
 * Building blocks of a subscription's summary (account drawer markup from the prototype), shared
 * by the drawer and the Main tab of the subscription record form. `ids` keep element ids unique
 * when both are on screen.
 */
interface Ids {
  stats?: string;
  spark?: string;
  sparkTitle?: string;
  details?: string;
  events?: string;
  historyTitle?: string;
}

export function SubscriptionStats({
  account: a,
  id,
}: {
  account: SubscriptionDetail;
  id?: string;
}) {
  const value = a.mrr || a.lastMrr;
  const figures = '/help/screens/account-details#figures';
  const stats: [string, string, string][] = [
    [a.mrr ? 'MRR' : 'MRR before churn', fmt.eurFull(value), '/help/kpis/mrr'],
    [
      a.billing === 'Monthly' || a.isTrial ? 'ARR' : 'Annual value',
      fmt.eurFull(value * 12),
      '/help/kpis/arr',
    ],
    ['Active users · 30 days', `${a.activeUsers30d} / ${a.users}`, '/help/kpis/active-users'],
    ['Published apps', `${a.publishedApps} / ${a.apps}`, '/help/kpis/published-apps'],
  ];
  return (
    <div className="stat-grid" id={id}>
      {stats.map(([label, text, kpi]) => (
        <div className="stat" key={label}>
          <div className="card__title-row">
            <span className="stat__label">{label}</span>
            <HelpBulb topics={[figures, kpi]} label={label} />
          </div>
          <span className="stat__value">{text}</span>
        </div>
      ))}
    </div>
  );
}

export function MrrSpark({ account: a, ids = {} }: { account: SubscriptionDetail; ids?: Ids }) {
  const max = Math.max(1, ...a.history);
  const n = a.history.length - 1 || 1;
  const spark = a.history
    .map(
      (v, i) => `${i ? 'L' : 'M'}${((i / n) * 400).toFixed(1)},${(74 - (v / max) * 66).toFixed(1)}`,
    )
    .join(' ');
  const titleId = ids.sparkTitle ?? 'spark-title';
  return (
    <section className="stack stack--tight" aria-labelledby={titleId}>
      <div className="card__title-row">
        <h3 className="section-title" id={titleId}>
          MRR, last 24 months
        </h3>
        <HelpBulb
          topics={[
            '/help/screens/account-details#mrr-last-24-months',
            '/help/kpis/mrr',
            '/help/data-model/monthly-mrr',
          ]}
          labelledBy={titleId}
        />
      </div>
      <svg
        className="spark"
        id={ids.spark}
        viewBox="0 0 400 80"
        preserveAspectRatio="none"
        role="img"
        aria-label="MRR history"
      >
        <path d={spark} className="trend-line" />
      </svg>
    </section>
  );
}

export function SubscriptionDetailsList({ items, id }: { items: [string, string][]; id?: string }) {
  return (
    <dl className="kv" id={id}>
      {items.map(([label, text]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{text}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SubscriptionHistory({
  account: a,
  ids = {},
}: {
  account: SubscriptionDetail;
  ids?: Ids;
}) {
  const events = a.isTrial
    ? [
        {
          month: a.started ? monthShort(monthIndex(a.started)) : '',
          label: 'Trial started',
          amount: '',
          type: '',
        },
      ]
    : a.events.map((e) => ({
        month: monthShort(monthIndex(e.month)),
        label: e.note,
        amount: fmt.signed(e.amount, true),
        type: e.type,
      }));
  const titleId = ids.historyTitle ?? 'history-title';
  return (
    <section className="stack stack--tight" aria-labelledby={titleId}>
      <div className="card__title-row">
        <h3 className="section-title" id={titleId}>
          Subscription history
        </h3>
        <HelpBulb
          topics={[
            '/help/screens/account-details#subscription-history',
            '/help/data-model/mrr-movement',
          ]}
          labelledBy={titleId}
        />
      </div>
      <ol className="timeline" id={ids.events}>
        {events.map((e, i) => (
          <li key={`${e.month}-${i}`}>
            <time>{e.month}</time>
            <span>{e.label}</span>
            <span className={e.type ? `num amount--${e.type}` : 'num'}>{e.amount}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
